import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isAdmin } from '@app/core/misc/isAdmin';
import { withFirstTeamIds } from '@app/core/misc/rxjs-operators';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { NoAssignedTeamDialog } from '@app/shared/components/no-assigned-team/no-assigned-team.dialog';
import { openDialog } from '@app/shared/misc/openDialog';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { DialogService } from 'primeng/dynamicdialog';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AuthStatus } from '../../entity/auth-status';
import { TranslatableEffect } from '../../i18n/TranslatableEffects';
import { Jwt } from '../../misc/jwt';
import { redirectToLogin } from '../../misc/login-routing';
import { BlAccountAuthService } from '../../services/auth/bl-account-auth.service';
import { SsoAuthService } from '../../services/auth/sso-auth.service';
import { UserService } from '../../services/user.service';
import { UserSettingsStorage } from '../../storage/user.settings.storage';
import { NoOperationAction } from '../core/core.actions';
import { i18nActions } from '../i18n/i18n.actions';
import { ToastActions } from '../toast/toast.actions';
import { LoginProviderType, SimpleUserType, UserType, UserTypeWithFirm } from '../types';
import { UserActions } from '../users/users.actions';
import { UserSelectors } from '../users/users.selectors';
import { AuthActions } from './auth.actions';
import { AuthSelectors } from './auth.selectors';

@Injectable()
export class AuthEffects extends TranslatableEffect {
  private uss = new UserSettingsStorage();

  initLegacyLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.legacyInitLogin),
      tap(() => this.setLoginProvider(LoginProviderType.LEGACY)),
      tap(() => this.blAccountAuthService.login()),
      map(() => AuthActions.loginSpinnerStart()),
    ),
  );

  initSSOLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initSsoLogin),
      tap(() => this.setLoginProvider(LoginProviderType.SSO)),
      switchMap(({ useBlAccountAuthenticator }) =>
        this.ssoAuthService
          .loginStart()
          .pipe(map(({ authorizationRequestUrl }) => ({ authorizationRequestUrl, useBlAccountAuthenticator }))),
      ),
      tap((response) => this.openSSOLoginUri(response)),
      mergeMap(() => [AuthActions.loginSpinnerStart()]),
      catchError(() => of(AuthActions.loginSpinnerStopp())),
    ),
  );

  waitForLegacyLogin$ = createEffect(() =>
    this.blAccountAuthService.authState.pipe(
      // eslint-disable-next-line no-undefined
      filter((token) => token !== undefined),
      map((token: string | null) => {
        if (token && Jwt.isValidToken(token)) {
          return AuthActions.legacyLoginSuccessful({ token });
        }
        return AuthActions.loginSpinnerStopp();
      }),
    ),
  );

  waitForSSOLogin$ = createEffect(() =>
    this.ssoAuthService.csrfToken.pipe(
      filter((csrf) => csrf !== null),
      map((csrf: string | null) => {
        if (csrf) {
          return AuthActions.ssoLoginSuccessful({ token: csrf });
        }
        return AuthActions.loginSpinnerStopp();
      }),
    ),
  );

  storeJwtTokenOnSuccessfulLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.legacyLoginSuccessful),
      tap(({ token }) => {
        // this.storeJwtAndUserInfo(token)
        this.uss.saveSetting('jwt', token);
      }),
      map(() => AuthActions.legacyAfterLoginSuccessful()),
    ),
  );

  storeCsrfTokenOnSuccessfulSSOLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ssoLoginSuccessful),
      tap(({ token }) => this.storeCsrfToken(token)),
      map(() => AuthActions.ssoAfterLoginSuccessful()),
    ),
  );

  toAfterLoginSuccessful$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.legacyAfterLoginSuccessful, AuthActions.ssoAfterLoginSuccessful),
      map(() => AuthActions.afterLoginSuccessful()),
    ),
  );

  clearPersistentStorageIfDifferentUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadCurrentUserSuccessful),
        tap(({ user }) => {
          if (!this.isSameFirmAsLastLogin(user) || !this.isSameUserAsLastLogin(user)) {
            const loginProvider = this.uss.loadSetting(UserSettingsStorage.LOGIN_PROVIDER, '');
            this.uss.clearSettings();
            this.uss.saveSetting(UserSettingsStorage.LOGIN_PROVIDER, loginProvider);
          }

          // // this.uss.saveSetting('jwt', jwt);
          // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_SERVICE, user.firm.service);
          // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_FIRM_NAME, user.firm.dataOwner.name);

          this.allInclusiveService.reloadLoggedInService();
        }),
      ),
    { dispatch: false },
  );

  routeToStartOnSuccessfullyLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCurrentUserSuccessful),
      withLatestFrom(this.store.select(AuthSelectors.selectRedirectUrl)),
      tap(([, redirectUrl]) => this.router.navigate([redirectUrl ?? 'start'])), // go to start page if success
      map(() => AuthActions.clearRedirectUrl()),
    ),
  );

  loadCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.afterLoginSuccessful, AuthActions.reloadCurrentAuthUser),
      exhaustMap(() =>
        this.userService.loadCurrentUserType().pipe(
          map((response) => {
            if (this.isUserTypeWithFirm(response)) {
              return AuthActions.loadCurrentUserSuccessful({ user: response });
            }
            const statusToRouteMap = {
              [AuthStatus.EON_USER_NOT_FOUND]: 'missingaccountlink',
              [AuthStatus.NO_FIRM_ACCESS]: 'missingfirms',
            };

            this.router.navigate([statusToRouteMap[response] ?? 'login']);
            return NoOperationAction();
          }),
          catchError((error: unknown) => of(AuthActions.loadCurrentUserFailed({ error }))),
        ),
      ),
    ),
  );

  selectFirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.selectFirm),
      exhaustMap(({ id }) =>
        this.userService.reloadWithSelectedFirm(id).pipe(
          map((user) => AuthActions.loadCurrentUserSuccessful({ user })),
          catchError((error: unknown) => of(AuthActions.loadCurrentUserFailed({ error }))),
        ),
      ),
    ),
  );

  reloadAuthUserIfNotExists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.reloadCurrentAuthUserIfNotLoaded),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectAuthUser)),
      filter(([, authUser]) => !authUser),
      map(() => AuthActions.reloadCurrentAuthUser()),
    ),
  );

  reset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resetAuthStateAndStorage, AuthActions.loadCurrentUserFailed),
        tap(() => this.uss.clearSessionStorage()),
        tap(() => this.userService.logout()),
        tap(() => redirectToLogin(this.router)),
      ),
    { dispatch: false },
  );

  setActiveLanguageFromCurrentUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCurrentUserSuccessful),
      map(({ user }) => user.languageCode),
      map((languageCode) => i18nActions.languageChange({ languageCode })),
    ),
  );

  persistSelectedLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(i18nActions.languageChanged),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectAuthUser)),
      map(([action, user]) => ({ ...user, languageCode: action.languageCode })),
      switchMap((userWithUpdatedLanguage) =>
        this.userService.saveUserType(userWithUpdatedLanguage).pipe(
          map(() => UserActions.loadAllUsers()), // reloads the users in case they go to the firmusers and update a user
          catchError(() => of(NoOperationAction)),
        ),
      ),
    ),
  );

  checkUsersWithouTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCurrentUserSuccessful),
      filter(({ user }) => isAdmin(user)),
      switchMap(() => withFirstTeamIds(this.store)),
      filter(({ ids }) => ids?.length > 1),
      concatLatestFrom(() => this.store.select(UserSelectors.activeUsersWithoutTeamsAsSimpleUser)),
      filter(([, users]) => Boolean(users?.length)),
      map(([, users]) => this.getUsersAsString(users)),
      map((detail) =>
        ToastActions.showWarnMessage({
          summary: this.translate('theseUsersIsMissingTeams'),
          detail,
          sticky: true,
        }),
      ),
    ),
  );

  checkUserWithouTeams$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadCurrentUserSuccessful),
        filter(({ user }) => !user.teams?.length),
        switchMap(() => withFirstTeamIds(this.store)),
        filter(({ ids }) => ids?.length > 1),
        tap(() => openDialog(this.dialogService, NoAssignedTeamDialog, {})),
      ),
    { dispatch: false },
  );

  // private storeJwtAndUserInfo = (jwt: string) => {
  //   const tokenPayload = Jwt.decodeToken(jwt);

  //   if (!this.isSameFirmAsLastLogin(tokenPayload) || !this.isSameUserAsLastLogin(tokenPayload)) {
  //     const loginProvider = this.uss.loadSetting(UserSettingsStorage.LOGIN_PROVIDER, '');
  //     this.uss.clearSettings();
  //     this.uss.saveSetting(UserSettingsStorage.LOGIN_PROVIDER, loginProvider);
  //   }

  //   this.uss.saveSetting('jwt', jwt);
  //   // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_SERVICE, tokenPayload.service);
  //   // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_FIRM_NAME, tokenPayload.dataOwner.name);

  //   // this.allInclusiveService.reloadLoggedInService();
  // };

  private storeCsrfToken = (token: string) => {
    this.uss.saveSetting(UserSettingsStorage.CSRF_TOKEN, token);
    // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_SERVICE, 'BL_T_A');
    // this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_FIRM_NAME, '');
  };

  private isSameFirmAsLastLogin = (user: UserType) =>
    Boolean(user) && user.firm.name === this.uss.loadSetting(UserSettingsStorage.LOGGED_IN_FIRM_NAME, '');

  private isSameUserAsLastLogin = (user: UserType) =>
    Boolean(user) && user.name === this.uss.loadSetting(UserSettingsStorage.LOGGED_IN_USER_NAME, '');

  private getUsersAsString = (users: SimpleUserType[]) => users.map((u) => `${u.initials} - ${u.name}`).join(', ');

  private openSSOLoginUri = ({
    authorizationRequestUrl,
    useBlAccountAuthenticator,
  }: {
    authorizationRequestUrl: string;
    useBlAccountAuthenticator: boolean;
  }) => {
    const [baseUrlPart, queryString] = authorizationRequestUrl.split('?');
    const queryParams = new URLSearchParams(queryString);

    // queryParams.append(CurityParams.UI_LOCALES, authData.locale);

    if (useBlAccountAuthenticator) {
      queryParams.append('acr_values', this.config.getValueFor('blauth').blAccountAcrValue);
    }

    // if (authData.register) {
    //   queryParams.append('register', 'true');
    // }

    /* adding acr_values with Bjorn ID to the query params ensures that the user will
        be redirected to the Bjorn ID login page (the initial way of logging in with Curity) */

    window.location.href = `${baseUrlPart}?${queryParams.toString()}`;
  };

  private setLoginProvider = (provider: LoginProviderType) => {
    this.uss.saveSetting(UserSettingsStorage.LOGIN_PROVIDER, provider);
  };

  private isUserTypeWithFirm = (data: UserType | AuthStatus): data is UserTypeWithFirm =>
    Boolean((data as UserTypeWithFirm)?.firm) && typeof (data as UserTypeWithFirm).firm.id === 'number';

  // eslint-disable-next-line max-params
  constructor(
    private router: Router,
    private blAccountAuthService: BlAccountAuthService,
    private userService: UserService,
    private dialogService: DialogService,
    private allInclusiveService: AllInclusiveService,
    private ssoAuthService: SsoAuthService,
  ) {
    super('AuthEffects');
  }
}
